import { Injectable } from '@angular/core';
import { USER_ID } from '@app/shared/constants/localstorage-keys';
import { LocalStorageItem } from '@app/shared/interfaces/local-storage.interface';
import { isEqual } from 'lodash';
import { catchError, debounce, debounceTime, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { DataService } from '../data/data.service';
import { UtilService } from './util.service';

@Injectable({
  providedIn: 'root',
})
export class BrowserLocalStorageService {
  // userId is now sourced from UserService, not persisted here
  private _userID!: string | null;
  private initialized = false;
  private _cachedLocalStorageData: LocalStorageItem[] = [];
  private _lastSaveTrigger$ = new Subject<void>();
  requestAfterLastSave$ = this._lastSaveTrigger$.pipe(
    debounceTime(300),
    switchMap(() =>
      this.requestAPILocalStorageData().pipe(
        tap((newData) => (this._cachedLocalStorageData = newData)),
        catchError(() => of(this._cachedLocalStorageData))
      )
    )
  );

  constructor(private dataService: DataService) {
    this.requestAfterLastSave$.subscribe();
  }

  /**
   * Initialize the local storage.
   * If the local storage has already been initialized, return the cached data.
   * Otherwise, it will initialize the local storage and return the data.
   * @returns An observable that emits the local storage data array.
   */
  initializeStorage(): Observable<LocalStorageItem[]> {
    if (this.initialized) {
      return of(this._cachedLocalStorageData);
    }

    this.initialized = true;
    return this.initLocalStorage();
  }

  /**
   * Initializes the local storage cache.
   *
   * This method requests the latest data from the API and stores it in the cache.
   * The cache is then used for all subsequent read operations to avoid unnecessary API calls.
   *
   * @remarks This method is idempotent and will not perform any action if the cache is already populated.
   */
  initLocalStorage(): Observable<LocalStorageItem[]> {
    return this.requestAPILocalStorageData().pipe(tap((value) => (this._cachedLocalStorageData = value)));
  }

  /**
   * Saves the given value under the given key in the local storage.
   * Before saving, it will encrypt both the key and the value.
   * If the value is already present in the local storage and the same as the given value,
   * it will not perform any action and return the current value.
   * If it differs, it will save the new value and trigger the {@link lastSaveTrigger$} observable.
   * @param key The key under which the value should be saved.
   * @param value The value to be saved.
   * @returns An observable that emits the saved value.
   */
  saveAPILocalStorage(key: string, value: any): Observable<any> {
    const url = 'Utils/savelocalstorage';
    const newValue = UtilService.encryptData(value);
    const currentValue = this.getAPILocalStorage(key);
    if (isEqual(currentValue, newValue)) return of(currentValue);
    return this.dataService.post(url, this.defineDataToSave(key, value)).pipe(
      tap(() => this._lastSaveTrigger$.next()),
      catchError((error) => {
        console.error('Error saving API local storage:', error);

        return of(error);
      })
    );
  }

  /**
   * Gets the value stored in the local storage under the given key.
   * If no key is provided, it will return the entire cache.
   * @param key The key for which the value should be retrieved.
   * @returns The saved value or undefined if no value is present.
   */
  getAPILocalStorage(key?: string): any | undefined {
    if (!key) {
      return this._cachedLocalStorageData;
    }
    const encriptedKey = UtilService.encryptData(key);
    const item: Record<string, any> | undefined = this._cachedLocalStorageData.find(
      (item) => item.key.toUpperCase() === encriptedKey.toUpperCase()
    );
    return item ? this.safeJsonParse(item.value) : undefined;
  }

  // Local Browser Storage
  public getItem(key: string, defaultValue?: any): any {
    let item = localStorage.getItem(UtilService.encryptData(this.getEncryptingKey(key)));
    if (item && item !== 'undefined') {
      item = UtilService.decryptData(item);
      //verify if item can do JSON.parse. (securityService.GetToken save as string)
if (item?.indexOf('{') === 0 || item?.indexOf('[') === 0 || item?.indexOf('"') === 0) {
  //I couldn't find any faster way to validate if item is json
  try {
    item = JSON.parse(item);
  } catch (e) {
    console.error('Failed to parse JSON from localStorage for key:', key, 'Value:', item, 'Error:', e);
    return defaultValue;
  }
}
      if (item === 'true' || item === 'false') {
        return item === 'true';
      } else {
        //convert to boolean result;
        return item;
      } //return string or object
    }

    return defaultValue || null;
  }

  public setItem(key: string, value: any) {
    localStorage.setItem(
      UtilService.encryptData(this.getEncryptingKey(key)),
      UtilService.encryptData(value)
    );
  }

  public removeItem(key: string) {
    localStorage.removeItem(UtilService.encryptData(this.getEncryptingKey(key)));
  }

  /**
   * Prepares the data to be saved in the API local storage.
   * It encrypts both the key and the value.
   * @param key - The key to be saved.
   * @param value - The value to be saved with the given key.
   * @returns An object with a single property with the encrypted key and the encrypted value.
   */
  private defineDataToSave(key: string, value: any) {
    const encriptedKey = UtilService.encryptData(key);
    const newValue = UtilService.encryptData(value);

    return {
      [encriptedKey]: newValue,
    };
  }

  private getEncryptingKey(key: string) {
    return `${this.userID}:${key}`;
  }

  /**
   * Requests the latest data from the API local storage.
   * It will return an empty array if there is any error.
   * @returns An observable that emits the local storage data array.
   */
  public requestAPILocalStorageData(): Observable<LocalStorageItem[]> {
    const url = 'Utils/getlocalstorage';
    return this.dataService.get(url).pipe(
      map((response: Response | any) => response),
      catchError(() => of([]))
    );
  }

  /**
   * Clears the API local storage.
   * If a key is provided, only the item with the given key will be cleared.
   * Otherwise, the entire cache will be cleared.
   * The method will return an observable that emits the result of the operation.
   * If there is any error, the observable will emit an empty array.
   * @param key - The key of the item to be cleared.
   * @returns An observable that emits the result of the operation.
   */
  public clearAPILocalStorage(key?: string): Observable<any> {
    const url = 'Utils/clearlocalstorage';
    if (key) {
      const encriptedKey = UtilService.encryptData(key);
      return this.dataService.delete(`${url}?key=${encriptedKey}`).pipe(
        tap(() => this._lastSaveTrigger$.next()),
        catchError(() => of([]))
      );
    }
    return this.dataService.delete(url).pipe(
      tap(() => this._lastSaveTrigger$.next()),
      catchError(() => of([]))
    );
  }

  /**
   * Safely parses a JSON string after decrypting it.
   *
   * This function attempts to decrypt the given value and parse it as JSON.
   * If parsing fails, it returns the decrypted value as is.
   *
   * @param value - The encrypted value to be decrypted and parsed.
   * @returns The parsed JSON object, or the decrypted value if parsing fails.
   */

  public safeJsonParse(value: any): any {
    try {
      const decryptedValue = UtilService.decryptData(value);
      const parsedValue = JSON.parse(decryptedValue);
      return parsedValue;
    } catch (e) {
      return UtilService.decryptData(value);
    }
  }

  /*
    Notes:
    1. AuthService was removed since it was causing a circular dependency issue.
    2. BrowserLocalStorageService Service constructor is being called again after postUserLogin()
    which is causing _userID to go undefined after being set
    Having it in localStorage will allow to get the value again
  */
  set userID(userID: string) {
    const localStorageUser = localStorage.getItem(UtilService.encryptData(USER_ID));

    if (!localStorageUser) {
      const encryptedKey = UtilService.encryptData(USER_ID);
        const encryptedData = UtilService.encryptData(userID);
      localStorage.setItem(encryptedKey, encryptedData);


       const localStorageUser2 = localStorage.getItem(UtilService.encryptData(USER_ID));
      const decrypt = UtilService.decryptData(localStorageUser2);

      this._userID = userID;
    }
  }

  get userID() {
    if (!this._userID) {
      this._userID = localStorage.getItem(UtilService.encryptData(USER_ID));
    }

    return UtilService.decryptData(this._userID);
  }
}
